import React, { useState } from 'react';

interface ThreatLocation {
  lat: number;
  lng: number;
  region: string;
  threats: number;
  severity: 'low' | 'medium' | 'high';
  details: string[];
}

interface FallbackMapProps {
  threatData: ThreatLocation[];
  height?: string;
  className?: string;
}

export const FallbackMap: React.FC<FallbackMapProps> = ({ 
  threatData, 
  height = "400px", 
  className = "" 
}) => {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getSeveritySize = (threats: number) => {
    return Math.max(8, Math.min(20, threats * 2));
  };

  const latLngToSvg = (lat: number, lng: number, width: number, height: number) => {
    // Convert lat/lng to SVG coordinates
    const x = ((lng + 180) / 360) * width;
    const y = ((90 - lat) / 180) * height;
    return { x, y };
  };

  return (
    <div className={`relative ${className}`} style={{ height, width: '100%' }}>
      <div className="relative w-full h-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-800 dark:to-slate-900">
        {/* Professional Interactive World Map */}
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1000 500"
          className="w-full h-full"
        >
          <defs>
            {/* Ocean gradient */}
            <linearGradient id="oceanGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#dbeafe"/>
              <stop offset="50%" stopColor="#bfdbfe"/>
              <stop offset="100%" stopColor="#93c5fd"/>
            </linearGradient>
            
            {/* Continent gradient */}
            <linearGradient id="continentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f8fafc"/>
              <stop offset="50%" stopColor="#e2e8f0"/>
              <stop offset="100%" stopColor="#cbd5e1"/>
            </linearGradient>
            
            {/* Grid pattern */}
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#e5e7eb" strokeWidth="0.5" opacity="0.3"/>
            </pattern>
          </defs>

          {/* Ocean background with grid */}
          <rect width="100%" height="100%" fill="url(#oceanGradient)" />
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Professional World Map */}
          <g fill="url(#continentGradient)" stroke="#64748b" strokeWidth="2" opacity="0.95">
            {/* North America */}
            <path d="M 80 100 L 200 80 L 250 120 L 240 160 L 200 180 L 160 170 L 120 150 L 90 130 Z" />
            {/* United States */}
            <path d="M 120 140 L 200 120 L 220 150 L 200 170 L 160 160 Z" />
            {/* Canada */}
            <path d="M 100 80 L 200 60 L 220 100 L 200 120 L 120 140 L 100 120 Z" />
            
            {/* Europe */}
            <path d="M 450 120 L 500 110 L 520 140 L 500 160 L 460 170 L 440 150 Z" />
            {/* UK */}
            <path d="M 420 140 L 440 130 L 450 150 L 430 160 Z" />
            {/* Scandinavia */}
            <path d="M 480 80 L 520 70 L 530 100 L 500 110 L 480 100 Z" />
            
            {/* Asia */}
            <path d="M 500 100 L 700 80 L 750 120 L 720 180 L 680 200 L 600 190 L 550 160 L 520 140 Z" />
            {/* China */}
            <path d="M 600 140 L 700 120 L 720 160 L 680 180 L 620 170 Z" />
            {/* India */}
            <path d="M 580 180 L 650 170 L 670 200 L 620 210 L 590 190 Z" />
            {/* Japan */}
            <path d="M 750 140 L 780 130 L 790 160 L 760 170 Z" />
            
            {/* Africa */}
            <path d="M 480 200 L 550 180 L 580 220 L 560 280 L 520 300 L 480 280 L 460 240 Z" />
            {/* North Africa */}
            <path d="M 480 200 L 550 180 L 560 220 L 500 240 Z" />
            {/* Sub-Saharan Africa */}
            <path d="M 500 240 L 560 220 L 580 280 L 520 300 L 500 280 Z" />
            
            {/* South America */}
            <path d="M 180 250 L 250 230 L 280 280 L 260 350 L 220 370 L 180 360 L 160 320 L 170 280 Z" />
            {/* Brazil */}
            <path d="M 200 280 L 260 260 L 280 320 L 240 340 L 200 320 Z" />
            {/* Argentina */}
            <path d="M 220 320 L 260 300 L 280 350 L 240 370 L 220 360 Z" />
            
            {/* Australia */}
            <path d="M 600 350 L 700 340 L 720 380 L 700 420 L 650 430 L 600 420 L 580 380 Z" />
            {/* New Zealand */}
            <path d="M 750 400 L 780 390 L 790 420 L 760 430 Z" />
            
            {/* Antarctica */}
            <path d="M 100 450 L 900 450 L 900 480 L 100 480 Z" />
          </g>

          {/* Interactive Threat Markers */}
          {threatData.map((threat, index) => {
            const { x, y } = latLngToSvg(threat.lat, threat.lng, 1000, 500);
            const size = getSeveritySize(threat.threats);
            const color = getSeverityColor(threat.severity);
            
            return (
              <g key={index}>
                {/* Outer glow effect */}
                <circle
                  cx={x}
                  cy={y}
                  r={size + 8}
                  fill="none"
                  stroke={color}
                  strokeWidth="3"
                  opacity="0.2"
                  className="animate-pulse"
                />
                {/* Pulsing ring */}
                <circle
                  cx={x}
                  cy={y}
                  r={size + 4}
                  fill="none"
                  stroke={color}
                  strokeWidth="2"
                  opacity="0.4"
                  className="animate-ping"
                />
                {/* Main threat marker */}
                <circle
                  cx={x}
                  cy={y}
                  r={size}
                  fill={color}
                  stroke="white"
                  strokeWidth="3"
                  className="cursor-pointer hover:scale-125 transition-all duration-200"
                  onMouseEnter={() => setHoveredRegion(threat.region)}
                  onMouseLeave={() => setHoveredRegion(null)}
                />
                {/* Threat count label */}
                <text
                  x={x}
                  y={y + 2}
                  textAnchor="middle"
                  fontSize="10"
                  fill="white"
                  fontWeight="bold"
                  className="pointer-events-none"
                >
                  {threat.threats}
                </text>
              </g>
            );
          })}

          {/* Connection Lines */}
          {threatData.length > 1 && (
            <g stroke="#6b7280" strokeWidth="2" opacity="0.3">
              {threatData.map((threat, index) => {
                const { x, y } = latLngToSvg(threat.lat, threat.lng, 1000, 500);
                return threatData.slice(index + 1).map((otherThreat, otherIndex) => {
                  const { x: x2, y: y2 } = latLngToSvg(otherThreat.lat, otherThreat.lng, 1000, 500);
                  return (
                    <line
                      key={`${index}-${otherIndex}`}
                      x1={x}
                      y1={y}
                      x2={x2}
                      y2={y2}
                      strokeDasharray="4,4"
                      className="animate-pulse"
                    />
                  );
                });
              })}
            </g>
          )}
        </svg>

        {/* Hovered region info */}
        {hoveredRegion && (
          <div className="absolute top-4 left-4 bg-white dark:bg-slate-800 rounded-lg shadow-lg p-3 z-10 border border-slate-200 dark:border-slate-700">
            <div className="text-sm font-medium text-slate-900 dark:text-white">
              {hoveredRegion}
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-400">
              Interactive threat region
            </div>
          </div>
        )}

        {/* Live data indicator */}
        <div className="absolute top-4 right-4 bg-purple-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg z-10">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
            Live Global Data
          </div>
        </div>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-white dark:bg-slate-800 rounded-lg shadow-lg p-3 z-10 border border-slate-200 dark:border-slate-700">
          <div className="text-xs font-medium text-slate-900 dark:text-white mb-2">Threat Severity</div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-xs text-slate-600 dark:text-slate-400">Low Risk</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-xs text-slate-600 dark:text-slate-400">Medium Risk</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-xs text-slate-600 dark:text-slate-400">High Risk</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
























